<template>
  <div
    class="IsoInvestmentAnimation"
  >
    <IsoChatOverlay
      ref="overlayRef"
      :vector="overlayVector"
    >
      <IsoChatGroup>
        <IsoChat
          ref="userChatRef"
          type="User"
          :fragments="['How are my', 'investments performing?']"
        />
      </IsoChatGroup>

      <IsoChatGroup>
        <IsoChat
          ref="betterChatRef"
          type="System"
          :fragments="['I found a new', 'portfolio blueprint', 'by @jonnie that', 'matches your goals.']"
        />
      </IsoChatGroup>

      <IsoChatGroup>
        <IsoChat
          ref="bestChatRef"
          type="System"
          :fragments="['Here’s', 'another', 'blueprint', 'by @lindsay:']"
        />
      </IsoChatGroup>

      <IsoChatGroup>
        <IsoChat
          ref="rebalanceChatRef"
          type="System"
          :fragments="['Want me', 'to rebalance', 'your portfolio', 'for you?']"
        />
        <IsoChat
          ref="confirmChatRef"
          type="User"
          :fragments="['Let’s do it']"
        />
      </IsoChatGroup>
    </IsoChatOverlay>
  </div>
</template>

<script lang="ts" setup>
import useCamera from "./useCamera";
import useDisposer from "./useDisposer";
import usePan from "./usePan";
import useThreeElement from "./useThreeElement";
import useViewport from "./useViewport";
import animate from "./animate";
import delay from "./delay";
import { easeInCubic, easeInOutCubic, easeOutCubic } from "@shared/lib/animation/easing";
import { Group, Object3D, Vector3 } from "three";
import { ref, toRefs } from "vue";
import IsoChat from "./IsoChat.vue";
import IsoChatGroup from "./IsoChatGroup.vue";
import IsoChatOverlay from "./IsoChatOverlay.vue";
import IsoInvestmentGraph from "./IsoInvestmentGraph";
import IsoPromptBlock from "./IsoPromptBlock";
import Colors from "@cosine/lib/Colors";

const props = defineProps<{
  parent: Object3D,
}>();

defineExpose({
  animateIn,
});

const {
  viewport,
  isTablet,
} = useViewport();

const {
  canvas,
} = useThreeElement();

const {
  parent,
} = toRefs(props);

const overlayRef = ref<InstanceType<typeof IsoChatOverlay>>();
const userChatRef = ref<InstanceType<typeof IsoChat>>();
const betterChatRef = ref<InstanceType<typeof IsoChat>>();
const bestChatRef = ref<InstanceType<typeof IsoChat>>();
const rebalanceChatRef = ref<InstanceType<typeof IsoChat>>();
const confirmChatRef = ref<InstanceType<typeof IsoChat>>();
const overlayVector = ref(new Vector3(0, 0, 0));

const {
  autoDispose,
} = useDisposer();
const {
  panPosition,
  animatePan,
} = usePan();
const {
  animateCameraZoom,
} = useCamera();

const group = new Group();
let performingBlock: IsoPromptBlock;
parent.value.add(group);

async function animateIn () {
  await animateChatBlock();
  await animatePortfolioGraph();
}

async function animateChatBlock () {
  overlayVector.value = new Vector3(0, 0, 0);

  performingBlock = autoDispose(new IsoPromptBlock(group));
  group.position.sub(panPosition.value);
  canvas.requestRender();

  overlayRef.value?.animateIn();
  await delay(700);
  await userChatRef.value?.animateIn();
  await performingBlock.animateThinking(canvas.requestRender);

  userChatRef.value?.animateOut();
  await delay(650);
  await overlayRef.value?.animateOut();
}

async function animatePortfolioGraph () {
  const portfolioGraph = autoDispose(new IsoInvestmentGraph(group, {
    color: Colors.purple300,
    minHeight: 1,
    maxHeight: 3,
  }));

  const betterGraph = autoDispose(new IsoInvestmentGraph(group, {
    color: Colors.teal300,
    minHeight: 1.5,
    maxHeight: 4,
  }));
  betterGraph.group.position.x = -1;

  const bestGraph = autoDispose(new IsoInvestmentGraph(group, {
    color: Colors.teal300,
    minHeight: 2,
    maxHeight: 5,
  }));
  bestGraph.group.position.x = -2;

  const originalPanPosition = panPosition.value.clone();
  const xOffset = isTablet.value ? -1 : 0;

  await Promise.all([
    portfolioGraph.animateIn(canvas.requestRender),
    animateCameraZoom({
      zoom: 0.75,
      duration: 1000,
    }),
    animatePan({
      position: originalPanPosition.clone().add(new Vector3(
        -xOffset,
        -2,
        xOffset,
      )),
      duration: 1000,
    }),
  ]);

  performingBlock.meshGroup.visible = false;

  const block = autoDispose(new IsoPromptBlock(group));
  block.meshGroup.position.x = -1;
  block.meshGroup.position.z = 2;
  block.meshGroup.position.y = -1.1;

  await Promise.all([
    animate((t) => {
      block.meshGroup.position.y = -1.1 + (0 + 1.1) * t;
      canvas.requestRender();
    }, {
      delay: 500,
      duration: 500,
      easing: easeOutCubic,
    }),
    animateCameraZoom({
      zoom: 1,
      duration: 1500,
    }),
    animatePan({
      position: originalPanPosition.clone().add(new Vector3(
        -(1 + block.meshGroup.position.x),
        0,
        1 - block.meshGroup.position.z,
      )),
      duration: 1500,
    }),
  ]);

  overlayVector.value = new Vector3(-1, 0, 1);
  overlayRef.value?.animateIn();
  await delay(700);

  await block.animateAnswering(({
    index,
  }) => {
    betterChatRef.value?.animateIn({
      index,
    });
  }, canvas.requestRender);

  const yOffsetIncrement = viewport.value.width < 600 ? 1 : 2;

  await Promise.all([
    betterGraph.animateIn(canvas.requestRender),
    animate((t) => {
      overlayVector.value = new Vector3(overlayVector.value.x, -t * yOffsetIncrement, overlayVector.value.z);
    }, {
      duration: 1500,
      easing: easeInOutCubic,
    }),
    animatePan({
      position: panPosition.value.clone().add(new Vector3(0, -yOffsetIncrement, 0)),
      duration: 1500,
    }),
  ]);

  await animate((t) => {
    const style = betterChatRef.value?.$el.style;
    if (style) { style.opacity = String(1 - t); }
  }, {
    delay: 500,
    duration: 1000,
  });

  await block.animateAnswering(({
    index,
  }) => {
    bestChatRef.value?.animateIn({
      index,
    });
  }, canvas.requestRender);

  await Promise.all([
    bestGraph.animateIn(canvas.requestRender),
    animate((t) => {
      overlayVector.value = new Vector3(overlayVector.value.x, -yOffsetIncrement - t * yOffsetIncrement, overlayVector.value.z);
    }, {
      duration: 1500,
      easing: easeInOutCubic,
    }),
    animatePan({
      position: panPosition.value.clone().add(new Vector3(0, -yOffsetIncrement, 0)),
      duration: 1500,
    }),
  ]);

  await animate((t) => {
    const style = bestChatRef.value?.$el.style;
    if (style) { style.opacity = String(1 - t); }
  }, {
    delay: 500,
    duration: 1000,
  });

  await block.animateAnswering(({
    index,
  }) => {
    rebalanceChatRef.value?.animateIn({
      index,
    });
  }, canvas.requestRender);

  await delay(1000);

  await Promise.all([confirmChatRef.value?.animateIn(), block.animateResponse(canvas.requestRender)]);

  await delay(500);

  Promise.all([rebalanceChatRef, confirmChatRef].map((chatRef) => chatRef.value?.animateOut()));

  await delay(650);

  await overlayRef.value?.animateOut();
  block.animateDown(canvas.requestRender);

  await delay(150);
  bestGraph.animateOut(canvas.requestRender);

  await delay(150);
  betterGraph.animateOut(canvas.requestRender);

  await delay(150);
  await Promise.all([
    portfolioGraph.animateOut(canvas.requestRender),
    animate((t) => {
      block.meshGroup.position.y = t * -1.1;
      canvas.requestRender();
    }, {
      duration: 750,
      easing: easeInCubic,
    }),
  ]);
}
</script>
